import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { ACCENT, FONT_ACCENT, TEXT_PRIMARY } from '../../tokens';

export interface EditableValueProps {
  value: number;
  /** Renders the number for display, e.g. `42` → `"42 people"`. */
  format: (value: number) => string;
  /** Parses typed text back to a number. Falls back to stripping non-numerics. */
  parse?: (text: string) => number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  style?: CSSProperties;
}

const VALUE_STYLE: CSSProperties = {
  fontFamily: FONT_ACCENT,
  fontStyle: 'italic',
  fontSize: '1.5rem',
  fontWeight: 700,
  color: TEXT_PRIMARY,
};

/**
 * A formatted number that becomes a text input on click.
 *
 * Lets someone type an exact figure instead of hunting for it with a slider.
 * Committed values are clamped to `[min, max]`; unparseable input is discarded.
 */
export function EditableValue({ value, format, parse, onChange, min, max, style }: EditableValueProps) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [editing]);

  const startEdit = (): void => {
    setText(format(value));
    setEditing(true);
  };

  const commit = (): void => {
    setEditing(false);
    const parsed = parse ? parse(text) : Number(text.replace(/[^0-9.-]/g, ''));
    if (Number.isNaN(parsed)) return;
    onChange(Math.max(min, Math.min(max, parsed)));
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') commit();
    if (event.key === 'Escape') setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={onKeyDown}
        style={{
          ...VALUE_STYLE,
          background: 'transparent',
          border: 'none',
          borderBottom: `1px solid ${ACCENT}`,
          width: '100%',
          textAlign: 'right',
          outline: 'none',
          padding: 0,
          margin: 0,
          ...style,
        }}
      />
    );
  }

  return (
    <span
      onClick={startEdit}
      style={{
        ...VALUE_STYLE,
        cursor: 'text',
        borderBottom: '1px solid transparent',
        transition: 'border-color 0.2s',
        ...style,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderBottomColor = 'rgba(255,107,0,0.3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderBottomColor = 'transparent';
      }}
    >
      {format(value)}
    </span>
  );
}
